import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { type CreateGreenBeanProfileDTO } from '../../../types/greenBean';
import { CreatableCombobox } from '../../../components/common/CreatableCombobox';
import { clsx } from 'clsx';
import { useLanguage } from '../../../features/language/useLanguage';

const COLORS = [
  'Blue Green', 'Bluish-Green', 'Green', 'Greenish', 
  'Yellow-Green', 'Pale Yellow', 'Yellowish', 'Brownish'
];

const GRADES = ['1', '2', '3', '4', '5'];

const AROMAS = [
  'Grassy / Herbal', 'Vegetal', 'Sweet / Hay-like', 'Fruity', 'Nutty',
  'Musty / Mouldy', 'Earthy / Dirty', 'Fermented / Sour', 'Baggy',
  'Chemical / Phenolic', 'Smoky'
];

interface GreenBeanQualitySectionProps {
  disabled?: boolean;
}

export const GreenBeanQualitySection: React.FC<GreenBeanQualitySectionProps> = ({ disabled = false }) => {
  const { t } = useLanguage();
  const { register, control, formState: { errors } } = useFormContext<{ green_bean_profile: CreateGreenBeanProfileDTO }>();

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-medium text-gray-900 border-b pb-2">{t('greenBean.quality.title')}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('greenBean.quality.grade')}</label>
          <Controller
            control={control}
            name="green_bean_profile.grade"
            rules={{ required: t('greenBean.quality.errors.gradeRequired') }}
            render={({ field }) => (
              <div className="flex gap-4">
                {GRADES.map((grade) => (
                  <label key={grade} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      {...field}
                      value={grade}
                      disabled={disabled}
                      checked={field.value === grade}
                      className={`h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      data-test-id={`green-bean-grade-radio-${grade}`}
                    />
                    <span className="ml-2 text-sm text-gray-700">{t('greenBean.quality.gradeLabel', { grade })}</span>
                  </label>
                ))}
              </div>
            )}
          />
        </div>

        <div>
          <Controller
            control={control}
            name="green_bean_profile.color"
            rules={{ required: t('greenBean.quality.errors.colorRequired') }}
            render={({ field }) => (
              <CreatableCombobox
                label={t('greenBean.quality.color')}
                value={field.value}
                onChange={field.onChange}
                options={COLORS}
                disabled={disabled}
                error={errors.green_bean_profile?.color?.message}
                dataTestId="green-bean-color-input"
              />
            )}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('greenBean.quality.moistureContent')}</label>
          <input
            type="number"
            step="0.01"
            disabled={disabled}
            {...register('green_bean_profile.moisture_content', { valueAsNumber: true })}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 ${disabled ? 'bg-gray-100' : ''}`}
            data-test-id="green-bean-moisture-input"
          />
        </div>

        <div className="flex items-center mt-6">
          <input
            type="checkbox"
            id="sample_physical_check"
            disabled={disabled}
            {...register('green_bean_profile.sample_physical_check')}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            data-test-id="green-bean-physical-check-input"
          />
          <label htmlFor="sample_physical_check" className="ml-2 block text-sm text-gray-900">
            {t('cupping.formSections.labels.physicalCheckPassed')}
          </label>
        </div>

        <div>
            <label className="block text-sm font-medium text-gray-700">{t('greenBean.quality.qcCupping')}</label>
            <select
                disabled={disabled}
                {...register('green_bean_profile.qc_cupping')}
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 ${disabled ? 'bg-gray-100' : ''}`}
                data-test-id="green-bean-qc-cupping-select"
            >
                <option value="">{t('greenBean.quality.selectType')}</option>
                <option value="Simplified">{t('cupping.formSections.mode.simplified')}</option>
                <option value="Full">{t('qcCupping.modeFull')}</option>
            </select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('greenBean.quality.aromaMulti')}</label>
          <Controller
            control={control}
            name="green_bean_profile.aroma"
            render={({ field }) => {
              const selectedAromas = Array.isArray(field.value) ? field.value : [];
              
              const toggleAroma = (aroma: string) => {
                if (disabled) return;
                const newAromas = selectedAromas.includes(aroma)
                  ? selectedAromas.filter(a => a !== aroma)
                  : [...selectedAromas, aroma];
                field.onChange(newAromas);
              };

              return (
                <div className="flex flex-wrap gap-2">
                  {AROMAS.map((aroma) => (
                    <button
                      key={aroma}
                      type="button"
                      onClick={() => toggleAroma(aroma)}
                      disabled={disabled}
                      data-test-id={`green-bean-aroma-button-${aroma}`}
                      className={clsx(
                        "px-3 py-1.5 rounded-full text-sm font-medium transition-colors border",
                        selectedAromas.includes(aroma)
                          ? "bg-primary-100 text-primary-800 border-primary-200"
                          : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50",
                        disabled && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      {aroma}
                    </button>
                  ))}
                </div>
              );
            }}
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700">{t('greenBean.quality.tasteProfile')}</label>
          <textarea
            disabled={disabled}
            {...register('green_bean_profile.taste_profile')}
            rows={3}
            placeholder={t('greenBean.quality.tasteProfilePlaceholder')}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 ${disabled ? 'bg-gray-100' : ''}`}
            data-test-id="green-bean-taste-profile-input"
          />
        </div>

      </div>
    </div>
  );
};
